import React, { useEffect, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  Grid,
  Chip,
  Typography,
  Box,
  Link,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import RefreshIcon from '@material-ui/icons/Refresh';

const useStyles = makeStyles(theme => ({
  metricCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginTop: theme.spacing(1),
  },
  metricLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  qualityGate: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  passed: {
    color: theme.palette.success.main,
  },
  failed: {
    color: theme.palette.error.main,
  },
  warning: {
    color: theme.palette.warning.main,
  },
}));

interface SonarQubeMetrics {
  qualityGateStatus: string;
  bugs: string;
  vulnerabilities: string;
  codeSmells: string;
  coverage: string;
  duplications: string;
  securityHotspots: string;
}

interface SonarQubeIssue {
  key: string;
  rule: string;
  severity: string;
  component: string;
  line?: number;
  message: string;
  type: string;
  status: string;
}

interface SonarQubeCardProps {
  entity?: Entity;
}

export const SonarQubeCard = ({ entity }: SonarQubeCardProps) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const [metrics, setMetrics] = useState<SonarQubeMetrics | null>(null);
  const [issues, setIssues] = useState<SonarQubeIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const projectKey =
    entity?.metadata.annotations?.['sonarqube.org/project-key'];

  useEffect(() => {
    if (!projectKey) {
      setLoading(false);
      return;
    }

    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);
      try {
        const backendUrl = config.getString('backend.baseUrl');

        // Fetch quality gate status
        const qualityGateResponse = await fetch(
          `${backendUrl}/api/proxy/sonarqube/qualitygates/project_status?projectKey=${encodeURIComponent(
            projectKey,
          )}`,
        );

        if (!qualityGateResponse.ok) {
          throw new Error(
            `Failed to fetch quality gate: ${qualityGateResponse.statusText}`,
          );
        }

        const qualityGateData = await qualityGateResponse.json();

        // Fetch measures
        const measuresResponse = await fetch(
          `${backendUrl}/api/proxy/sonarqube/measures/component?component=${encodeURIComponent(
            projectKey,
          )}&metricKeys=bugs,vulnerabilities,code_smells,coverage,duplicated_lines_density,security_hotspots`,
        );

        if (!measuresResponse.ok) {
          throw new Error(
            `Failed to fetch measures: ${measuresResponse.statusText}`,
          );
        }

        const measuresData = await measuresResponse.json();

        const getMeasureValue = (key: string) => {
          const measure = measuresData.component?.measures?.find(
            (m: any) => m.metric === key,
          );
          return measure?.value || '0';
        };

        setMetrics({
          qualityGateStatus: qualityGateData.projectStatus?.status || 'NONE',
          bugs: getMeasureValue('bugs'),
          vulnerabilities: getMeasureValue('vulnerabilities'),
          codeSmells: getMeasureValue('code_smells'),
          coverage: getMeasureValue('coverage'),
          duplications: getMeasureValue('duplicated_lines_density'),
          securityHotspots: getMeasureValue('security_hotspots'),
        });

        // Fetch issues
        const issuesResponse = await fetch(
          `${backendUrl}/api/proxy/sonarqube/issues/search?componentKeys=${encodeURIComponent(
            projectKey,
          )}&statuses=OPEN,CONFIRMED,REOPENED&ps=20&s=SEVERITY&asc=false`,
        );

        if (issuesResponse.ok) {
          const issuesData = await issuesResponse.json();
          setIssues(issuesData.issues || []);
        }
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [projectKey, config, refreshTrigger]);

  if (!projectKey) {
    return (
      <InfoCard title="Code Quality">
        <Typography>
          No SonarQube project key found. Add the{' '}
          <code>sonarqube.org/project-key</code> annotation to your component.
        </Typography>
      </InfoCard>
    );
  }

  if (loading) {
    return (
      <InfoCard title="Code Quality">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Code Quality">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  if (!metrics) {
    return (
      <InfoCard title="Code Quality">
        <Typography>No metrics available</Typography>
      </InfoCard>
    );
  }

  const getQualityGateIcon = () => {
    switch (metrics.qualityGateStatus) {
      case 'OK':
        return <CheckCircleIcon className={classes.passed} />;
      case 'ERROR':
        return <ErrorIcon className={classes.failed} />;
      case 'WARN':
        return <WarningIcon className={classes.warning} />;
      default:
        return null;
    }
  };

  const getQualityGateColor = () => {
    switch (metrics.qualityGateStatus) {
      case 'OK':
        return 'primary';
      case 'ERROR':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const sonarQubeUrl = config.getOptionalString('sonarqube.baseUrl');
  const projectUrl = sonarQubeUrl
    ? `${sonarQubeUrl}/dashboard?id=${encodeURIComponent(projectKey)}`
    : undefined;

  return (
    <InfoCard
      title="Code Quality"
      subheader={`Project: ${projectKey}`}
      action={
        <Box display="flex" alignItems="center">
          {projectUrl && (
            <Link
              href={projectUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginRight: '8px' }}
            >
              View in SonarQube
            </Link>
          )}
          <Tooltip title="Refresh data">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              size="small"
              style={{
                color: '#1976d2',
                border: '1px solid #1976d2',
                padding: '6px',
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      }
    >
      <Box className={classes.qualityGate}>
        {getQualityGateIcon()}
        <Chip
          label={`Quality Gate: ${metrics.qualityGateStatus}`}
          color={getQualityGateColor()}
          size="medium"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6} md={4}>
          <Box className={classes.metricCard}>
            <Typography variant="body2" className={classes.metricLabel}>
              Bugs
            </Typography>
            <Typography className={classes.metricValue}>
              {metrics.bugs}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} md={4}>
          <Box className={classes.metricCard}>
            <Typography variant="body2" className={classes.metricLabel}>
              Vulnerabilities
            </Typography>
            <Typography className={classes.metricValue}>
              {metrics.vulnerabilities}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} md={4}>
          <Box className={classes.metricCard}>
            <Typography variant="body2" className={classes.metricLabel}>
              Security Hotspots
            </Typography>
            <Typography className={classes.metricValue}>
              {metrics.securityHotspots}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} md={4}>
          <Box className={classes.metricCard}>
            <Typography variant="body2" className={classes.metricLabel}>
              Code Smells
            </Typography>
            <Typography className={classes.metricValue}>
              {metrics.codeSmells}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} md={4}>
          <Box className={classes.metricCard}>
            <Typography variant="body2" className={classes.metricLabel}>
              Coverage
            </Typography>
            <Typography className={classes.metricValue}>
              {parseFloat(metrics.coverage).toFixed(1)}%
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} md={4}>
          <Box className={classes.metricCard}>
            <Typography variant="body2" className={classes.metricLabel}>
              Duplications
            </Typography>
            <Typography className={classes.metricValue}>
              {parseFloat(metrics.duplications).toFixed(1)}%
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {issues.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Open Issues ({issues.length})
          </Typography>
          <Box style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>
                    Severity
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>
                    Message
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>File</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Line</th>
                </tr>
              </thead>
              <tbody>
                {issues.map(issue => {
                  const fileName =
                    issue.component.split(':').pop() || issue.component;
                  const getSeverityColor = (severity: string) => {
                    switch (severity) {
                      case 'BLOCKER':
                      case 'CRITICAL':
                        return '#d32f2f';
                      case 'MAJOR':
                        return '#f57c00';
                      case 'MINOR':
                        return '#fbc02d';
                      case 'INFO':
                        return '#1976d2';
                      default:
                        return '#757575';
                    }
                  };

                  const getTypeIcon = (type: string) => {
                    switch (type) {
                      case 'BUG':
                        return '🐛';
                      case 'VULNERABILITY':
                        return '🔒';
                      case 'CODE_SMELL':
                        return '💩';
                      case 'SECURITY_HOTSPOT':
                        return '🔥';
                      default:
                        return '📋';
                    }
                  };

                  return (
                    <tr
                      key={issue.key}
                      style={{
                        borderBottom: '1px solid #eee',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <td style={{ padding: '12px' }}>
                        <Chip
                          label={issue.severity}
                          size="small"
                          style={{
                            backgroundColor: getSeverityColor(issue.severity),
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ fontSize: '1.2em', marginRight: '4px' }}>
                          {getTypeIcon(issue.type)}
                        </span>
                        {issue.type.replace('_', ' ')}
                      </td>
                      <td style={{ padding: '12px', maxWidth: '400px' }}>
                        <Typography variant="body2">{issue.message}</Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Typography
                          variant="body2"
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '0.85em',
                          }}
                        >
                          {fileName}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {issue.line && (
                          <Chip
                            label={`L${issue.line}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </InfoCard>
  );
};
